import React, { useRef, useState, useCallback, useMemo } from "react";
import { api, fileUrl } from "../lib/apiClient";
import { WORLD_W, WORLD_H, uid } from "../lib/blueprint";
import { buildElevationField, contourLevels, contourLines, elevationColor } from "../lib/contours";
import { insetPolygon, pointsAttr, rectToPolygon, parseCourse, courseLabel, metesToPolygon } from "../lib/geo";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import {
  Mountain, Upload, Loader2, Compass, TreePine, Car, Waves, MapPin, Trash2,
  ArrowRight, Sparkles, X, Ruler, MousePointer2, Landmark, Search, Waypoints, Spline, Route,
} from "lucide-react";
import { toast } from "sonner";

const SLOPES = ["Flat", "Downhill", "Uphill", "Cross-slope"];

// Site feature marker kinds
const FEATURES = {
  driveway: { label: "Driveway", color: "#8B949E", Icon: Car },
  trees: { label: "Trees", color: "#34D399", Icon: TreePine },
  water: { label: "Water", color: "#38BDF8", Icon: Waves },
  corner: { label: "Corner", color: "#F59E0B", Icon: MapPin },
};

const TOOLS = [
  { id: "move", label: "Select / Move", Icon: MousePointer2 },
  { id: "elevation", label: "Spot Elevation", Icon: Mountain },
  { id: "driveway", label: "Driveway", Icon: Car },
  { id: "trees", label: "Trees", Icon: TreePine },
  { id: "water", label: "Water", Icon: Waves },
  { id: "corner", label: "Lot Corner", Icon: MapPin },
  { id: "boundary", label: "Property Line", Icon: Spline },
  { id: "metes", label: "Metes & Bounds", Icon: Route },
];

export default function SiteMap({ site, setSite, lot, setLot, city, onContinue, worldW = WORLD_W, worldH = WORLD_H, onAutoFit, onZoningReport }) {
  const svgRef = useRef(null);
  const fileRef = useRef(null);
  const [tool, setTool] = useState("move");
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [selId, setSelId] = useState(null);
  const [showContours, setShowContours] = useState(true);
  const [showSb, setShowSb] = useState(true);
  const [address, setAddress] = useState("");
  const [fetching, setFetching] = useState(false);
  const [cal, setCal] = useState(null); // {a:{x,y}, b:{x,y}} calibration segment
  const [calFeet, setCalFeet] = useState("");
  const [mbForm, setMbForm] = useState({ ns: "N", deg: "", min: "", sec: "", ew: "W", dist: "" });
  const [mbPaste, setMbPaste] = useState("");
  const drag = useRef(null);

  const elevations = site.spot_elevations || [];
  const features = site.features || [];
  const orient = Number(site.orientation_deg) || 0;
  const sb = site.setbacks || { front: 0, side: 0, rear: 0 };
  const mapUrl = site.image_path ? fileUrl(site.image_path) : null;
  const metes = site.metes || { pob: null, courses: [] };

  const field = useMemo(() => buildElevationField(elevations, { w: worldW, h: worldH }), [elevations, worldW, worldH]);
  const contours = useMemo(() => {
    if (!field) return null;
    return { field, lines: contourLines(field, contourLevels(field.min, field.max, 6)) };
  }, [field]);

  const patchSite = (patch) => setSite((s) => ({ ...s, ...patch }));

  const toFt = useCallback((e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * worldW;
    const y = ((e.clientY - rect.top) / rect.height) * worldH;
    return { x: Math.min(Math.max(0, x), worldW), y: Math.min(Math.max(0, y), worldH) };
  }, [worldW, worldH]);

  const uploadMap = async (f) => {
    if (!f) return;
    if (!/(jpeg|jpg|png|webp)/i.test(f.type)) { toast.error("Use a JPEG, PNG or WEBP image"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/ai/upload-site-map", fd, { headers: { "Content-Type": "multipart/form-data" } });
      patchSite({ image_path: res.data.site_map_path });
      toast.success("Site map uploaded — trace your house on it in the Blueprint tab");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Site map upload failed");
    } finally { setUploading(false); }
  };

  const fetchFromAddress = async () => {
    if (!address.trim()) { toast.error("Type a property address first"); return; }
    setFetching(true);
    try {
      const res = await api.post("/ai/site-from-address", { address, city: city || "" });
      patchSite({ image_path: res.data.site_map_path });
      if (res.data.width_ft && res.data.depth_ft) setLot({ ...lot, w: Number(res.data.width_ft), h: Number(res.data.depth_ft) });
      toast.success(res.data.note || "Site map fetched for that address");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not fetch a site map for that address");
    } finally { setFetching(false); }
  };

  const analyze = async () => {
    if (!site.image_path) { toast.error("Upload a site map image first"); return; }
    setAnalyzing(true);
    try {
      const start = await api.post("/ai/analyze-site", { site_map_path: site.image_path, city: city || "Custom" });
      const tid = start.data.task_id;
      const deadline = Date.now() + 120000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const st = await api.get(`/ai/site-status/${tid}`);
        if (st.data.status === "completed") {
          const r = st.data.result || {};
          patchSite({
            slope_type: SLOPES.includes(r.slope_type) ? r.slope_type : site.slope_type,
            slope_pct: Number(r.slope_pct) || site.slope_pct || 0,
            orientation_deg: Number.isFinite(Number(r.orientation_deg)) ? Number(r.orientation_deg) : orient,
            ai_summary: r.summary || "",
            ai_features: (r.features || []).map((f) => `${f.kind}: ${f.note}`),
          });
          toast.success("AI analyzed your site");
          break;
        }
        if (st.data.status === "failed") { toast.error(st.data.error || "Site analysis failed"); break; }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Site analysis failed");
    } finally { setAnalyzing(false); }
  };

  const onDown = (e) => {
    const pt = toFt(e);
    if (tool === "calibrate") {
      if (!cal || cal.b) setCal({ a: pt, b: null });
      else setCal({ a: cal.a, b: pt });
      return;
    }
    if (tool === "move") { setSelId(null); return; }
    if (tool === "boundary") {
      patchSite({ boundary: [...(site.boundary || []), { x: +pt.x.toFixed(1), y: +pt.y.toFixed(1) }] });
      return;
    }
    if (tool === "metes") {
      setMetes({ pob: { x: +pt.x.toFixed(1), y: +pt.y.toFixed(1) } });
      return;
    }
    if (tool === "elevation") {
      const m = { id: uid(), x: Math.round(pt.x), y: Math.round(pt.y), z: 0 };
      patchSite({ spot_elevations: [...elevations, m] });
      setSelId(m.id);
      return;
    }
    if (FEATURES[tool]) {
      const m = { id: uid(), kind: tool, x: Math.round(pt.x), y: Math.round(pt.y) };
      patchSite({ features: [...features, m] });
      setSelId(m.id);
    }
  };

  const startDrag = (e, id, kind) => {
    if (tool !== "move") return;
    e.stopPropagation();
    setSelId(id);
    drag.current = { id, kind };
  };

  const onMove = (e) => {
    if (!drag.current) return;
    const pt = toFt(e);
    const { id, kind } = drag.current;
    setSite((s) => {
      if (kind === "elev") return { ...s, spot_elevations: (s.spot_elevations || []).map((m) => m.id === id ? { ...m, x: Math.round(pt.x), y: Math.round(pt.y) } : m) };
      if (kind === "bound") return { ...s, boundary: (s.boundary || []).map((v, i) => i === id ? { x: +pt.x.toFixed(1), y: +pt.y.toFixed(1) } : v) };
      return { ...s, features: (s.features || []).map((m) => m.id === id ? { ...m, x: Math.round(pt.x), y: Math.round(pt.y) } : m) };
    });
  };
  const onUp = () => { drag.current = null; };

  const setElevZ = (id, z) => patchSite({ spot_elevations: elevations.map((m) => m.id === id ? { ...m, z: Number(z) || 0 } : m) });
  const delElev = (id) => patchSite({ spot_elevations: elevations.filter((m) => m.id !== id) });
  const delFeature = (id) => patchSite({ features: features.filter((m) => m.id !== id) });

  const setSb = (k, v) => patchSite({ setbacks: { ...sb, [k]: Math.max(0, Number(v) || 0) } });

  // --- Metes & Bounds ---
  const setMetes = (patch) => patchSite({ metes: { ...metes, ...patch } });
  const parsePaste = () => {
    const c = parseCourse(mbPaste);
    if (!c || !c.dist) { toast.error('Couldn\'t read that call — try like N29°42\'41"W 124.00'); return; }
    setMetes({ courses: [...(metes.courses || []), c] });
    setMbPaste("");
    toast.success(`Added ${courseLabel(c)}`);
  };
  const addFormCourse = () => {
    const c = { ns: mbForm.ns, deg: Number(mbForm.deg) || 0, min: Number(mbForm.min) || 0, sec: Number(mbForm.sec) || 0, ew: mbForm.ew, dist: Number(mbForm.dist) || 0 };
    if (!c.dist) { toast.error("Enter a distance in feet"); return; }
    setMetes({ courses: [...(metes.courses || []), c] });
    setMbForm({ ...mbForm, deg: "", min: "", sec: "", dist: "" });
  };
  const delCourse = (i) => setMetes({ courses: (metes.courses || []).filter((_, k) => k !== i) });
  const drawMetes = () => {
    if (!(metes.courses || []).length) { toast.error("Add at least one course first"); return; }
    const pob = metes.pob || { x: lot.x + lot.w * 0.15, y: lot.y + lot.h * 0.85 };
    patchSite({ boundary: metesToPolygon(pob, metes.courses), metes: { ...metes, pob } });
    toast.success("Property boundary drawn from survey courses");
  };
  const mbPreview = (tool === "metes" && metes.pob && (metes.courses || []).length) ? metesToPolygon(metes.pob, metes.courses) : null;
  const mbClosure = mbPreview && mbPreview.length > 2
    ? Math.hypot(mbPreview[mbPreview.length - 1].x - mbPreview[0].x, mbPreview[mbPreview.length - 1].y - mbPreview[0].y) : null;

  const applyCalibration = () => {
    if (!cal || !cal.b) { toast.error("Click two points along a known boundary first"); return; }
    const dist = Math.hypot(cal.b.x - cal.a.x, cal.b.y - cal.a.y);
    const real = Number(calFeet);
    if (!dist || !real) { toast.error("Enter the real length in feet"); return; }
    const factor = real / dist;
    setLot({ ...lot, w: Math.round(lot.w * factor), h: Math.round(lot.h * factor) });
    // keep placed elevations / features aligned to the survey after rescaling
    patchSite({
      spot_elevations: elevations.map((m) => ({ ...m, x: +(m.x * factor).toFixed(2), y: +(m.y * factor).toFixed(2) })),
      features: features.map((m) => ({ ...m, x: +(m.x * factor).toFixed(2), y: +(m.y * factor).toFixed(2) })),
    });
    setCal(null); setCalFeet(""); setTool("move");
    toast.success(`Scale locked — that line is now ${Math.round(real)} ft`);
  };

  // buildable envelope from setbacks (drawn on the lot)
  const env = (sb.front || sb.side || sb.rear)
    ? { x: lot.x + (sb.side || 0), y: lot.y + (sb.front || 0), w: Math.max(1, lot.w - 2 * (sb.side || 0)), h: Math.max(1, lot.h - (sb.front || 0) - (sb.rear || 0)) }
    : null;
  // scale bar: pick a round number of feet ~ 1/5 of lot width
  const barFt = Math.max(5, Math.round((lot.w / 5) / 5) * 5) || 10;

  const bpoly = site.boundary || [];
  const bInset = bpoly.length >= 3 && (sb.front || sb.side || sb.rear)
    ? insetPolygon(bpoly, Math.max(sb.side || 0, sb.front || 0, 3)) : null;

  const nax = lot.x + Math.min(5, lot.w * 0.08), nay = lot.y + Math.min(5, lot.h * 0.08);
  const uScale = worldH / 60; // keep glyphs readable as world grows

  return (
    <div className="flex-1 flex overflow-hidden" data-testid="site-map-workspace">
      {/* Map canvas */}
      <div className="flex-1 relative bg-[#0B1017] overflow-hidden flex flex-col">
        {/* tool rail */}
        <div className="flex items-center gap-1.5 px-3 py-2 border-b border-[#30363D] bg-[#161B22] shrink-0 overflow-x-auto" data-testid="site-tool-rail">
          {TOOLS.map((t) => (
            <button key={t.id} onClick={() => setTool(t.id)} data-testid={`site-tool-${t.id}`} title={t.label}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${tool === t.id ? "bg-[#F43F5E] text-white" : "text-[#8B949E] hover:bg-[#21262D]"}`}>
              <t.Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
          <div className="w-px h-5 bg-[#30363D] mx-1" />
          <button onClick={() => setShowContours((v) => !v)} disabled={elevations.length < 3} data-testid="site-contours-toggle" title="Shade the slope from spot elevations (needs 3+ points)"
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${elevations.length < 3 ? "text-[#484F58] cursor-not-allowed" : showContours ? "bg-[#F59E0B] text-[#0D1117]" : "text-[#8B949E] hover:bg-[#21262D]"}`}>
            <Waypoints className="w-3.5 h-3.5" /> Contours
          </button>
          <button onClick={() => setShowSb((v) => !v)} data-testid="site-setbacks-toggle" title="Show the buildable setback lines"
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${showSb ? "bg-[#F59E0B] text-[#0D1117]" : "text-[#8B949E] hover:bg-[#21262D]"}`}>
            <Ruler className="w-3.5 h-3.5" /> Setbacks
          </button>
          <button onClick={() => { setTool(tool === "calibrate" ? "move" : "calibrate"); setCal(null); }} data-testid="site-calibrate-tool" title="Lock the scale: click two points on a known boundary, then enter its real length"
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${tool === "calibrate" ? "bg-[#38BDF8] text-[#0D1117]" : "text-[#8B949E] hover:bg-[#21262D]"}`}>
            <Ruler className="w-3.5 h-3.5" /> Calibrate scale
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6 flex items-center justify-center grid-dots">
          {!mapUrl ? (
            <div onClick={() => fileRef.current?.click()} data-testid="site-upload-dropzone"
              className="w-full max-w-2xl aspect-[4/3] rounded-2xl border-2 border-dashed border-[#30363D] bg-[#0D1117] flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-[#F43F5E] transition-colors text-center px-8">
              {uploading ? <Loader2 className="w-10 h-10 text-[#F43F5E] animate-spin" /> : <Upload className="w-10 h-10 text-[#6E7681]" />}
              <div className="font-heading font-semibold text-lg text-[#F0F6FC]">Upload your site map</div>
              <p className="text-sm text-[#8B949E] max-w-sm">Drop a land survey, plot plan, topographic map or satellite image of the lot. You'll define elevations and trace the house on top of it.</p>
              <span className="text-xs font-mono-cad text-[#6E7681]">JPEG · PNG · WEBP</span>
            </div>
          ) : (
            <svg ref={svgRef} viewBox={`0 0 ${worldW} ${worldH}`} data-testid="site-map-svg"
              className="w-full rounded-xl border border-[#30363D]" style={{ maxHeight: "78vh", background: "#0D1117", cursor: tool === "move" ? "default" : "crosshair", touchAction: "none" }}
              onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}>
              {/* the survey image mapped onto the lot rectangle at real scale */}
              <image href={mapUrl} x={lot.x} y={lot.y} width={lot.w} height={lot.h} preserveAspectRatio="xMidYMid meet" opacity="0.95" />
              {/* lot boundary */}
              <rect x={lot.x} y={lot.y} width={lot.w} height={lot.h} fill="none" stroke="#F43F5E" strokeWidth={0.4 * uScale} strokeDasharray={`${1.4 * uScale} ${0.9 * uScale}`} data-testid="site-lot-rect" />
              <text x={lot.x + 0.6} y={lot.y - 0.6} fill="#F43F5E" fontSize={1.6 * uScale} fontFamily="JetBrains Mono, monospace" fontWeight="700">LOT {Math.round(lot.w)}′ × {Math.round(lot.h)}′</text>

              {/* upper-floor cantilever projecting over the slope */}
              {site.cantilever && (
                <g data-testid="site-cantilever" style={{ pointerEvents: "none" }}>
                  <rect x={site.cantilever.x} y={site.cantilever.y} width={site.cantilever.w} height={site.cantilever.h}
                    fill="rgba(56,189,248,0.10)" stroke="#38BDF8" strokeWidth={0.4 * uScale} strokeDasharray={`${1.2 * uScale} ${0.7 * uScale}`} />
                  <text x={site.cantilever.x + site.cantilever.w / 2} y={site.cantilever.y + site.cantilever.h / 2}
                    fill="#38BDF8" fontSize={1.3 * uScale} fontWeight="800" textAnchor="middle" fontFamily="JetBrains Mono, monospace">CANTILEVER</text>
                </g>
              )}

              {/* top-of-slope grade break + downhill no-build zone */}
              {site.downhill_zone && site.downhill_zone.length >= 3 && (
                <g data-testid="site-downhill-zone" style={{ pointerEvents: "none" }}>
                  <polygon points={pointsAttr(site.downhill_zone)} fill="rgba(245,158,11,0.14)" stroke="none" />
                </g>
              )}
              {site.top_of_slope && site.top_of_slope.length >= 2 && (
                <g data-testid="site-top-of-slope" style={{ pointerEvents: "none" }}>
                  <polyline points={pointsAttr(site.top_of_slope)} fill="none" stroke="#F59E0B" strokeWidth={0.5 * uScale} strokeDasharray={`${1.6 * uScale} ${0.9 * uScale}`} />
                  <text x={site.top_of_slope[0].x + 2} y={site.top_of_slope[0].y - 1.2} fill="#F59E0B" fontSize={1.5 * uScale} fontWeight="800" fontFamily="JetBrains Mono, monospace">TOP OF SLOPE — NO BUILD DOWNHILL</text>
                </g>
              )}

              {/* driveway apron (paved strip from street to garage) */}
              {site.driveway && (
                <g data-testid="site-driveway" style={{ pointerEvents: "none" }}>
                  <rect x={site.driveway.x} y={site.driveway.y} width={site.driveway.w} height={site.driveway.h}
                    fill="#8B949E" fillOpacity="0.28" stroke="#C9D1D9" strokeWidth={0.3 * uScale} strokeDasharray={`${0.8 * uScale} ${0.6 * uScale}`} rx={0.4 * uScale} />
                  <text x={site.driveway.x + site.driveway.w / 2} y={site.driveway.y + site.driveway.h / 2}
                    fill="#F0F6FC" fontSize={1.3 * uScale} fontWeight="700" textAnchor="middle" fontFamily="JetBrains Mono, monospace"
                    transform={site.driveway.h > site.driveway.w ? `rotate(-90 ${site.driveway.x + site.driveway.w / 2} ${site.driveway.y + site.driveway.h / 2})` : ""}>DRIVEWAY</text>
                </g>
              )}

              {/* setback buildable envelope (rectangular — hidden when an angled boundary is drawn) */}
              {showSb && env && bpoly.length < 3 && (
                <g style={{ pointerEvents: "none" }} data-testid="site-setback-envelope">
                  <rect x={env.x} y={env.y} width={env.w} height={env.h} fill="#F59E0B" fillOpacity="0.07" stroke="#F59E0B" strokeWidth={0.35 * uScale} strokeDasharray={`${1.6 * uScale} ${1 * uScale}`} />
                  <text x={env.x + 0.6} y={env.y + 1.8 * uScale} fill="#F59E0B" fontSize={1.4 * uScale} fontWeight="700" fontFamily="JetBrains Mono, monospace">BUILDABLE · F{sb.front || 0}′ S{sb.side || 0}′ R{sb.rear || 0}′</text>
                </g>
              )}

              {/* elevation contours (shaded field + iso-lines) */}
              {showContours && contours && (
                <g style={{ pointerEvents: "none" }} data-testid="site-contours">
                  {contours.field.grid.map((row, j) => row.map((v, i) => (
                    <rect key={`c${i}-${j}`} x={i * contours.field.cell - contours.field.cell / 2} y={j * contours.field.cell - contours.field.cell / 2}
                      width={contours.field.cell} height={contours.field.cell} fill={elevationColor(v, contours.field.min, contours.field.max)} opacity="0.32" />
                  )))}
                  {contours.lines.map((lvl, k) => (
                    <g key={`l${k}`}>
                      {lvl.segs.map((s, m) => (
                        <line key={m} x1={s[0][0]} y1={s[0][1]} x2={s[1][0]} y2={s[1][1]} stroke="#0D1117" strokeOpacity="0.55" strokeWidth="0.18" />
                      ))}
                      {lvl.segs[0] && (
                        <text x={lvl.segs[0][0][0]} y={lvl.segs[0][0][1]} fill="#0D1117" fontSize="1.1" fontWeight="700" fontFamily="JetBrains Mono, monospace">{Math.round(lvl.level)}′</text>
                      )}
                    </g>
                  ))}
                </g>
              )}

              {/* angled property-line boundary */}
              {bpoly.length > 0 && (
                <g data-testid="site-boundary">
                  {bpoly.length >= 2 && (
                    <polygon points={pointsAttr(bpoly)} fill="none" stroke="#F43F5E" strokeWidth={0.4 * uScale} strokeDasharray={`${1.4 * uScale} ${0.9 * uScale}`} style={{ pointerEvents: "none" }} />
                  )}
                  {bInset && (
                    <polygon points={pointsAttr(bInset)} fill="#F59E0B" fillOpacity="0.08" stroke="#F59E0B" strokeWidth={0.35 * uScale} strokeDasharray={`${1.6 * uScale} ${1 * uScale}`} style={{ pointerEvents: "none" }} data-testid="site-buildable-polygon" />
                  )}
                  {bpoly.map((v, i) => (
                    <circle key={i} cx={v.x} cy={v.y} r={0.9 * uScale} fill="#F43F5E" stroke="#0D1117" strokeWidth={0.25 * uScale}
                      data-testid={`site-boundary-vertex-${i}`} onPointerDown={(e) => startDrag(e, i, "bound")} style={{ cursor: tool === "move" ? "move" : "crosshair" }} />
                  ))}
                </g>
              )}

              {/* metes & bounds live preview */}
              {mbPreview && (
                <g style={{ pointerEvents: "none" }} data-testid="site-metes-preview">
                  <polyline points={pointsAttr(mbPreview)} fill="rgba(56,189,248,0.06)" stroke="#38BDF8" strokeWidth={0.4 * uScale} strokeDasharray={`${1 * uScale} ${0.6 * uScale}`} />
                  {mbPreview.map((v, i) => (
                    <circle key={i} cx={v.x} cy={v.y} r={0.55 * uScale} fill="#38BDF8" />
                  ))}
                </g>
              )}
              {tool === "metes" && metes.pob && (
                <g transform={`translate(${metes.pob.x} ${metes.pob.y}) scale(${uScale})`} style={{ pointerEvents: "none" }} data-testid="site-metes-pob">
                  <circle r="1.4" fill="#0D1117" stroke="#34D399" strokeWidth="0.5" />
                  <circle r="0.6" fill="#34D399" />
                  <text x="2" y="0.4" fill="#34D399" fontSize="1.5" fontWeight="800" fontFamily="JetBrains Mono, monospace">POB</text>
                </g>
              )}

              {/* north arrow (rotates with orientation) */}
              <g transform={`translate(${nax} ${nay}) scale(${uScale}) rotate(${orient})`} style={{ pointerEvents: "none" }} data-testid="site-north-arrow">
                <circle r="4" fill="#0D1117" fillOpacity="0.7" stroke="#38BDF8" strokeWidth="0.3" />
                <path d="M 0 -3.4 L 1.4 1 L 0 0.2 L -1.4 1 Z" fill="#38BDF8" />
                <text x="0" y="-2.0" fill="#F0F6FC" fontSize="1.5" fontWeight="800" textAnchor="middle" fontFamily="Outfit, sans-serif">N</text>
              </g>

              {/* features */}
              {features.map((m) => {
                const cfg = FEATURES[m.kind] || FEATURES.corner;
                const sel = m.id === selId;
                return (
                  <g key={m.id} transform={`translate(${m.x} ${m.y}) scale(${uScale})`} data-testid={`site-feature-${m.id}`}
                    onPointerDown={(e) => startDrag(e, m.id, "feat")} style={{ cursor: tool === "move" ? "move" : "crosshair" }}>
                    <circle r="1.6" fill="#0D1117" stroke={cfg.color} strokeWidth={sel ? 0.6 : 0.4} />
                    <circle r="0.7" fill={cfg.color} />
                    <text x="2.2" y="0.5" fill={cfg.color} fontSize="1.4" fontWeight="700" fontFamily="JetBrains Mono, monospace" style={{ pointerEvents: "none" }}>{cfg.label}</text>
                  </g>
                );
              })}

              {/* spot elevations */}
              {elevations.map((m) => {
                const sel = m.id === selId;
                return (
                  <g key={m.id} transform={`translate(${m.x} ${m.y}) scale(${uScale})`} data-testid={`site-elevation-${m.id}`}
                    onPointerDown={(e) => startDrag(e, m.id, "elev")} style={{ cursor: tool === "move" ? "move" : "crosshair" }}>
                    <path d="M 0 0 L 1.1 -1.9 L -1.1 -1.9 Z" fill="#F59E0B" stroke={sel ? "#F0F6FC" : "#0D1117"} strokeWidth="0.2" />
                    <text x="1.6" y="-1.4" fill="#F59E0B" fontSize="1.5" fontWeight="800" fontFamily="JetBrains Mono, monospace" style={{ pointerEvents: "none" }}>{m.z}′</text>
                  </g>
                );
              })}

              {/* calibration segment */}
              {cal && cal.a && (
                <g style={{ pointerEvents: "none" }} data-testid="site-cal-line">
                  <line x1={cal.a.x} y1={cal.a.y} x2={(cal.b || cal.a).x} y2={(cal.b || cal.a).y} stroke="#38BDF8" strokeWidth={0.4 * uScale} strokeDasharray={`${1 * uScale} ${0.6 * uScale}`} />
                  <circle cx={cal.a.x} cy={cal.a.y} r={0.8 * uScale} fill="#38BDF8" />
                  {cal.b && <circle cx={cal.b.x} cy={cal.b.y} r={0.8 * uScale} fill="#38BDF8" />}
                </g>
              )}

              {/* scale bar */}
              <g transform={`translate(${lot.x + 1} ${lot.y + lot.h - 2 * uScale})`} style={{ pointerEvents: "none" }} data-testid="site-scale-bar">
                <line x1="0" y1="0" x2={barFt} y2="0" stroke="#F0F6FC" strokeWidth={0.4 * uScale} />
                <line x1="0" y1={-0.8 * uScale} x2="0" y2={0.8 * uScale} stroke="#F0F6FC" strokeWidth={0.4 * uScale} />
                <line x1={barFt} y1={-0.8 * uScale} x2={barFt} y2={0.8 * uScale} stroke="#F0F6FC" strokeWidth={0.4 * uScale} />
                <text x={barFt / 2} y={-1.2 * uScale} fill="#F0F6FC" fontSize={1.4 * uScale} fontWeight="700" textAnchor="middle" fontFamily="JetBrains Mono, monospace">{barFt} ft</text>
              </g>
            </svg>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="site-file-input" onChange={(e) => uploadMap(e.target.files[0])} />
          {tool === "calibrate" && (
            <div className="absolute top-16 left-1/2 -translate-x-1/2 glass rounded-xl border border-[#38BDF8]/50 px-3 py-2 flex items-center gap-2" data-testid="site-cal-panel">
              <span className="text-xs text-[#8B949E]">{!cal || !cal.b ? "Click two points along a known boundary" : "Real length:"}</span>
              {cal && cal.b && (
                <>
                  <Input type="number" value={calFeet} onChange={(e) => setCalFeet(e.target.value)} placeholder="ft" className="h-7 w-20 bg-[#0D1117] border-[#30363D] font-mono-cad text-xs" data-testid="site-cal-feet" />
                  <Button size="sm" onClick={applyCalibration} data-testid="site-cal-apply" className="h-7 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold text-xs">Lock scale</Button>
                </>
              )}
            </div>
          )}
          {tool === "boundary" && (
            <div className="absolute top-16 left-1/2 -translate-x-1/2 glass rounded-xl border border-[#F43F5E]/50 px-3 py-2 flex items-center gap-2" data-testid="site-boundary-panel">
              <span className="text-xs text-[#8B949E]">Click the lot corners to trace angled property lines</span>
              <Button size="sm" onClick={() => patchSite({ boundary: rectToPolygon(lot) })} data-testid="site-boundary-seed" className="h-7 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold text-xs">From lot</Button>
              <Button size="sm" variant="outline" onClick={() => patchSite({ boundary: [] })} data-testid="site-boundary-clear" className="h-7 border-[#30363D] bg-transparent text-xs">Clear</Button>
            </div>
          )}
          {tool === "metes" && (
            <div className="absolute top-16 left-4 w-[19rem] glass rounded-xl border border-[#38BDF8]/50 p-3 space-y-2.5 max-h-[80vh] overflow-y-auto" data-testid="site-metes-panel">
              <div className="flex items-center gap-2">
                <Route className="w-4 h-4 text-[#38BDF8]" />
                <span className="text-xs font-heading font-semibold text-[#F0F6FC]">Metes &amp; Bounds</span>
              </div>
              <p className="text-[11px] text-[#6E7681] leading-relaxed">
                {metes.pob ? "Point of Beginning set — add each survey course below, then draw." : "Click the map to drop your Point of Beginning (POB). North is up."}
              </p>

              <div className="space-y-1">
                <Label className="text-[10px] text-[#8B949E]">Paste a survey call</Label>
                <div className="flex gap-1.5">
                  <Input value={mbPaste} onChange={(e) => setMbPaste(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") parsePaste(); }} placeholder={`N29°42'41"W 124.00`} className="h-8 bg-[#0D1117] border-[#30363D] text-xs font-mono-cad" data-testid="site-metes-paste" />
                  <Button size="sm" onClick={parsePaste} data-testid="site-metes-paste-add" className="h-8 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] text-xs font-semibold">Add</Button>
                </div>
              </div>

              <div className="space-y-1.5 border-t border-[#30363D] pt-2">
                <Label className="text-[10px] text-[#8B949E]">Or build it</Label>
                <div className="flex items-center gap-1">
                  <div className="flex rounded-md overflow-hidden border border-[#30363D]">
                    {["N", "S"].map((q) => (
                      <button key={q} onClick={() => setMbForm({ ...mbForm, ns: q })} data-testid={`site-metes-ns-${q}`} className={`px-2 py-1 text-xs font-mono-cad ${mbForm.ns === q ? "bg-[#38BDF8] text-[#0D1117]" : "text-[#8B949E]"}`}>{q}</button>
                    ))}
                  </div>
                  <Input type="number" value={mbForm.deg} onChange={(e) => setMbForm({ ...mbForm, deg: e.target.value })} placeholder="°" className="h-8 w-12 bg-[#0D1117] border-[#30363D] text-xs font-mono-cad px-1.5" data-testid="site-metes-deg" />
                  <Input type="number" value={mbForm.min} onChange={(e) => setMbForm({ ...mbForm, min: e.target.value })} placeholder="′" className="h-8 w-12 bg-[#0D1117] border-[#30363D] text-xs font-mono-cad px-1.5" data-testid="site-metes-min" />
                  <Input type="number" value={mbForm.sec} onChange={(e) => setMbForm({ ...mbForm, sec: e.target.value })} placeholder="″" className="h-8 w-12 bg-[#0D1117] border-[#30363D] text-xs font-mono-cad px-1.5" data-testid="site-metes-sec" />
                  <div className="flex rounded-md overflow-hidden border border-[#30363D]">
                    {["E", "W"].map((q) => (
                      <button key={q} onClick={() => setMbForm({ ...mbForm, ew: q })} data-testid={`site-metes-ew-${q}`} className={`px-2 py-1 text-xs font-mono-cad ${mbForm.ew === q ? "bg-[#38BDF8] text-[#0D1117]" : "text-[#8B949E]"}`}>{q}</button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Input type="number" value={mbForm.dist} onChange={(e) => setMbForm({ ...mbForm, dist: e.target.value })} placeholder="distance (ft)" className="h-8 bg-[#0D1117] border-[#30363D] text-xs font-mono-cad" data-testid="site-metes-dist" />
                  <Button size="sm" onClick={addFormCourse} data-testid="site-metes-add" className="h-8 bg-[#34D399] hover:bg-[#10B981] text-[#0D1117] text-xs font-semibold whitespace-nowrap">Add course</Button>
                </div>
              </div>

              {(metes.courses || []).length > 0 && (
                <div className="space-y-1 border-t border-[#30363D] pt-2" data-testid="site-metes-list">
                  {metes.courses.map((c, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-[11px] font-mono-cad text-[#C9D1D9]">
                      <span className="text-[#6E7681] w-4">{i + 1}</span>
                      <span className="flex-1">{courseLabel(c)}</span>
                      <button onClick={() => delCourse(i)} data-testid={`site-metes-del-${i}`} className="text-[#6E7681] hover:text-[#EF4444]"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                  {mbClosure != null && (
                    <p className={`text-[10px] ${mbClosure < 1 ? "text-[#34D399]" : "text-[#F59E0B]"}`} data-testid="site-metes-closure">
                      {mbClosure < 1 ? "✓ Boundary closes" : `Closure gap: ${mbClosure.toFixed(2)} ft (add the closing course)`}
                    </p>
                  )}
                </div>
              )}

              <div className="flex gap-1.5 border-t border-[#30363D] pt-2">
                <Button size="sm" onClick={drawMetes} data-testid="site-metes-draw" className="flex-1 h-8 bg-[#F43F5E] hover:bg-[#E11D48] text-white text-xs font-semibold">Draw property line</Button>
                <Button size="sm" variant="outline" onClick={() => setMetes({ courses: [], pob: null })} data-testid="site-metes-clear" className="h-8 border-[#30363D] bg-transparent text-xs">Clear</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right control rail */}
      <aside className="w-72 shrink-0 border-l border-[#30363D] bg-[#161B22] p-4 overflow-y-auto" data-testid="site-controls">
        <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-[#8B949E] mb-3 flex items-center gap-2"><Mountain className="w-4 h-4 text-[#F43F5E]" /> Site & Land</h3>

        {mapUrl && (
          <button onClick={() => fileRef.current?.click()} data-testid="site-replace-btn"
            className="w-full mb-4 text-xs text-[#38BDF8] hover:text-[#7DD3FC] flex items-center justify-center gap-1.5 py-1.5 rounded-md border border-[#30363D] hover:border-[#38BDF8]">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Replace site map
          </button>
        )}

        {/* Fetch site map from address */}
        <div className="space-y-1.5 mb-4">
          <Label className="text-xs text-[#8B949E] flex items-center gap-1.5"><Search className="w-3.5 h-3.5" /> Auto-site from address</Label>
          <Input value={address} onChange={(e) => setAddress(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") fetchFromAddress(); }} placeholder="e.g. 350 W New York St, Indianapolis" className="h-8 bg-[#0D1117] border-[#30363D] text-xs" data-testid="site-address-input" />
          <Button onClick={fetchFromAddress} disabled={fetching} data-testid="site-address-fetch-btn" className="w-full h-8 bg-[#34D399] hover:bg-[#10B981] text-[#0D1117] font-semibold text-xs">
            {fetching ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Fetching site map…</> : <><MapPin className="w-3.5 h-3.5 mr-1.5" /> Pull satellite site map</>}
          </Button>
        </div>

        {/* Lot dimensions */}
        <div className="space-y-2 mb-4">
          <Label className="text-xs text-[#8B949E] flex items-center gap-1.5"><Ruler className="w-3.5 h-3.5" /> Lot dimensions (ft)</Label>
          <div className="flex items-center gap-1.5">
            <Input type="number" value={Math.round(lot.w)} onChange={(e) => setLot({ ...lot, w: Math.max(10, Number(e.target.value) || 10) })} className="h-8 bg-[#0D1117] border-[#30363D] font-mono-cad text-xs" data-testid="site-lot-width" />
            <span className="text-[#6E7681] text-xs">×</span>
            <Input type="number" value={Math.round(lot.h)} onChange={(e) => setLot({ ...lot, h: Math.max(10, Number(e.target.value) || 10) })} className="h-8 bg-[#0D1117] border-[#30363D] font-mono-cad text-xs" data-testid="site-lot-depth" />
          </div>
        </div>

        {/* Orientation */}
        <div className="space-y-2 mb-4">
          <Label className="text-xs text-[#8B949E] flex items-center gap-1.5"><Compass className="w-3.5 h-3.5" /> Orientation — North at {orient}°</Label>
          <input type="range" min="0" max="359" value={orient} onChange={(e) => patchSite({ orientation_deg: Number(e.target.value) })} className="w-full accent-[#38BDF8]" data-testid="site-orientation-slider" />
        </div>

        {/* Setbacks */}
        <div className="space-y-2 mb-4">
          <Label className="text-xs text-[#8B949E] flex items-center gap-1.5"><Ruler className="w-3.5 h-3.5" /> Setbacks (ft) — front / side / rear</Label>
          <div className="grid grid-cols-3 gap-1.5">
            <Input type="number" value={sb.front || 0} onChange={(e) => setSb("front", e.target.value)} className="h-8 bg-[#0D1117] border-[#30363D] font-mono-cad text-xs" data-testid="site-setback-front" placeholder="Front" />
            <Input type="number" value={sb.side || 0} onChange={(e) => setSb("side", e.target.value)} className="h-8 bg-[#0D1117] border-[#30363D] font-mono-cad text-xs" data-testid="site-setback-side" placeholder="Side" />
            <Input type="number" value={sb.rear || 0} onChange={(e) => setSb("rear", e.target.value)} className="h-8 bg-[#0D1117] border-[#30363D] font-mono-cad text-xs" data-testid="site-setback-rear" placeholder="Rear" />
          </div>
          <p className="text-[10px] text-[#6E7681]">Your survey notes a 15 ft front and 6 ft side yard.</p>
        </div>

        {/* Slope */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div>
            <Label className="text-xs text-[#8B949E]">Slope</Label>
            <Select value={site.slope_type || "Flat"} onValueChange={(v) => patchSite({ slope_type: v })}>
              <SelectTrigger data-testid="site-slope-select" className="h-8 bg-[#0D1117] border-[#30363D] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
                {SLOPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-[#8B949E]">Grade %</Label>
            <Input type="number" value={site.slope_pct || 0} onChange={(e) => patchSite({ slope_pct: Math.max(0, Number(e.target.value) || 0) })} className="h-8 bg-[#0D1117] border-[#30363D] font-mono-cad text-xs" data-testid="site-grade-input" />
          </div>
        </div>

        {/* AI analyze */}
        <Button onClick={analyze} disabled={analyzing || !site.image_path} data-testid="site-analyze-btn" className="w-full mb-2 bg-[#A855F7] hover:bg-[#9333EA] text-white font-semibold">
          {analyzing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing land…</> : <><Sparkles className="w-4 h-4 mr-2" /> Analyze with AI</>}
        </Button>
        {site.ai_summary && (
          <p className="text-[11px] text-[#8B949E] leading-relaxed border-l-2 border-[#A855F7] pl-2 mb-2 animate-fade-up" data-testid="site-ai-summary">{site.ai_summary}</p>
        )}
        {(site.ai_features || []).length > 0 && (
          <ul className="text-[11px] text-[#6E7681] mb-4 space-y-0.5 list-disc pl-4">
            {site.ai_features.map((f, i) => <li key={i}>{f}</li>)}
          </ul>
        )}

        {/* Spot elevations list */}
        {elevations.length > 0 && (
          <div className="mb-4">
            <Label className="text-xs text-[#8B949E] flex items-center gap-1.5 mb-1.5"><Mountain className="w-3.5 h-3.5 text-[#F59E0B]" /> Spot Elevations</Label>
            <div className="space-y-1.5" data-testid="site-elevation-list">
              {elevations.map((m, i) => (
                <div key={m.id} className="flex items-center gap-1.5">
                  <span className="text-[11px] font-mono-cad text-[#6E7681] w-6">#{i + 1}</span>
                  <Input type="number" value={m.z} onChange={(e) => setElevZ(m.id, e.target.value)} className="h-7 bg-[#0D1117] border-[#30363D] font-mono-cad text-xs" data-testid={`site-elev-input-${i}`} />
                  <span className="text-[11px] text-[#6E7681]">ft</span>
                  <button onClick={() => delElev(m.id)} className="text-[#6E7681] hover:text-[#EF4444]" data-testid={`site-elev-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Features list */}
        {features.length > 0 && (
          <div className="mb-4">
            <Label className="text-xs text-[#8B949E] flex items-center gap-1.5 mb-1.5"><Landmark className="w-3.5 h-3.5 text-[#34D399]" /> Site Features</Label>
            <div className="flex flex-wrap gap-1.5" data-testid="site-feature-list">
              {features.map((m) => {
                const cfg = FEATURES[m.kind] || FEATURES.corner;
                return (
                  <span key={m.id} className="text-[11px] px-2 py-1 rounded-md bg-[#0D1117] border border-[#30363D] font-mono-cad flex items-center gap-1" style={{ color: cfg.color }}>
                    {cfg.label}
                    <button onClick={() => delFeature(m.id)} className="text-[#6E7681] hover:text-[#EF4444]"><X className="w-3 h-3" /></button>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        <Button onClick={onContinue} data-testid="site-continue-btn" className="w-full bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold">
          Continue to Blueprint <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <Button onClick={onAutoFit} variant="outline" data-testid="site-autofit-btn" className="border-[#30363D] bg-transparent text-xs hover:border-[#38BDF8] hover:text-[#38BDF8]"><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Auto-Fit House</Button>
          <Button onClick={onZoningReport} variant="outline" data-testid="site-zoning-btn" className="border-[#30363D] bg-transparent text-xs hover:border-[#34D399] hover:text-[#34D399]"><Landmark className="w-3.5 h-3.5 mr-1.5" /> Zoning PDF</Button>
        </div>
        <p className="text-[11px] text-[#6E7681] mt-2 leading-relaxed">Your site map stays as a faint background layer in the Blueprint tab so you can trace the house directly on the land. Toggle it in the Layers panel.</p>
      </aside>
    </div>
  );
}
